import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  HostListener,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { QuantityControl } from '@softapps/lager/core';
import {
  BackNavigationService,
  HeaderComponent,
  MengenEingabeComponent,
  SOFTLINE_FEATURE_TITLE,
  Step,
  StepHeaderComponent,
  TitleStore,
} from '@softline/application';
import { Store } from '@softline/core';
import { UiCoreModule } from '@softline/ui-core';
import { SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL } from '../../lagerkorrektur-artikel.shared';
import { LagerkorrekturArtikelStore } from '../../store';

@Component({
  selector: 'soft-mengen-auswahl',
  imports: [
    CommonModule,
    UiCoreModule,
    MengenEingabeComponent,
    StepHeaderComponent,
    HeaderComponent,
    QuantityControl,
  ],
  templateUrl: './mengen-auswahl.page.html',
  styleUrls: ['./mengen-auswahl.page.css'],
})
export class MengenAuswahlPage implements OnInit {
  lagerplatz$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
    LagerkorrekturArtikelStore.getters.lagerplatz
  );
  artikel$ = this.store.observe(
    SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
    LagerkorrekturArtikelStore.getters.artikel
  );

  readonly steps: Step[] = [
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.LAGERPLATZ_SCANNEN.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.ARTIKEL_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.MENGEN_AUSWAHL.DESCRIPTION',
    },
    {
      header: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.TITLE',
      subHeader: '#LAGERKORREKTUR_ARTIKEL.PAGES.UMBUCHUNGSARTIKEL_AUSWAHL.DESCRIPTION',
    },
  ];

  private readonly productQuantity = toSignal(this.artikel$);
  private readonly quantityChanged = signal(false);
  private readonly quantityControl = viewChild(QuantityControl);

  readonly canContinue = computed(() => {
    const artikelMenge = this.productQuantity();
    if (!artikelMenge) return false;
    if (artikelMenge.arteheingabePflicht === true) return this.quantityChanged();
    return true;
  });

  constructor(
    private store: Store,
    private router: Router,
    private backNavigationService: BackNavigationService
  ) {
    // When `arteheingabePflicht` === true we open the modal instantly
    // `quantityChanged` determines if the quantity has been changed or not (e.g. modal dismissed)
    // Based on `arteheingabePflicht` and `quantityChanged` we can determine if the user can continue
    effect(async () => {
      const artikelMenge = this.productQuantity();
      const comp = this.quantityControl();
      if (!artikelMenge || !comp) return;

      if (
        artikelMenge?.arteheingabe &&
        artikelMenge?.arteheingabePflicht === true &&
        !untracked(() => this.quantityChanged())
      ) {
        await untracked(() => comp.openUnitEntryModal());
      }
    });
  }

  ngOnInit(): void {
    this.backNavigationService.set(this);
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setTitle,
      '#LAGERKORREKTUR_ARTIKEL.TITLE'
    );
  }

  ngOnDestroy() {
    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setTitle, '');
    this.backNavigationService.set(undefined);
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur-artikel/artikel-auswahl']);
  }

  async onMengeChange(params: {
    quantity: number | null | undefined;
    baseQuantity?: number | null;
    unitEntryId?: number | null;
  }): Promise<void> {
    this.quantityChanged.set(true);

    const artikel = this.store.get(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.getters.artikel
    );
    this.store.commit(
      SOFTLINE_FEATURE_LAGERKORREKTUR_ARTIKEL,
      LagerkorrekturArtikelStore.mutations.setArtikel,
      {
        ...artikel,
        menge: params.baseQuantity ?? 0,
        menge_lg: params.quantity ?? 0,
        idarteheingabe: params?.unitEntryId ?? undefined,
      }
    );
  }

  @HostListener('window:keydown.enter', ['$event'])
  async onSubmit(): Promise<void> {
    await this.router.navigate(['/lagerkorrektur-artikel', 'umbuchungsartikel-auswahl']);
  }
}
